import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule, ValidationErrors,
    Validators,
} from '@angular/forms';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectChange as MatSelectChange, MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SelectSearchOption } from '../dave-utils-module/select-search/components/select-search/select-search.component';
import { SelectSearchModule } from '../dave-utils-module/select-search/select-search.module';
import { Address, stringSearch } from '../helper/helper';
import {
    MatLegacyPaginatorIntl as MatPaginatorIntl,
    MatLegacyPaginatorModule,
} from '@angular/material/legacy-paginator';
import { GermanPaginatorIntl } from '../services/german-paginator-intl.service';
import { PageEvent } from '@angular/material/paginator';
import { CustomerDataService } from '../dave-data-module/services/customer-data.service';
import {
    SelectSearchPagedOptionsComponent
} from '../dave-utils-module/select-search/components/select-search-paged-options/select-search-paged-options.component';

export const AddressSearchPageSize = 50;
type AddressOption = SelectSearchOption<{ searchValues?: string[]; value: Address }>;


@Component({
    selector: 'app-address-form',
    templateUrl: './address-form.component.html',
    styleUrls: ['./address-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: AddressFormComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: AddressFormComponent,
        },
        { provide: MatPaginatorIntl, useClass: GermanPaginatorIntl }
    ],
    standalone: true,
    imports: [CommonModule, MatInputModule, ReactiveFormsModule, SelectSearchModule, MatSelectModule, NgxMatSelectSearchModule, FontAwesomeModule, ScrollingModule, MatLegacyPaginatorModule, SelectSearchPagedOptionsComponent],
})
export class AddressFormComponent implements ControlValueAccessor, OnDestroy {
    // @Input() legend: string;
    @Input() SearchAddressOptions: SelectSearchOption<{ searchValues?: string[]; value: Address }>[] | null = null;


    @Input() set required(value: boolean) {
        const validators = value ? [Validators.required] : [];
        (['Street', 'PostalCode', 'City', 'Country'] as const).forEach(field => {
            this.form.controls[field].setValidators(validators);
            this.form.controls[field].updateValueAndValidity();
        });
        this.onValidatorChange?.();
    }

    addressOptions$ = new BehaviorSubject<AddressOption[]>([]);
    addressLoading$ = new BehaviorSubject(false);
    addressPageIndex$ = new BehaviorSubject(0);
    addressPageCount$ = new BehaviorSubject(0);

    private currentSearch = '';
    private currentPageIndex = 0;
    private pageLastIds: (number | undefined)[] = [undefined];

    serverSearch = async (search: string) => {
        this.currentSearch = search;
        this.pageLastIds = [undefined];
        this.currentPageIndex = 0;
        this.addressPageIndex$.next(0);
        await this.fetchAddressPage(undefined, true);
    };

    addressPageChange = async (event: PageEvent) => {
        const newPageIndex = event.pageIndex;
        if (newPageIndex === this.currentPageIndex) return;
        if (newPageIndex > 0 && this.pageLastIds[newPageIndex] === undefined) return;

        const lastId = this.pageLastIds[newPageIndex];
        this.currentPageIndex = newPageIndex;
        this.addressPageIndex$.next(newPageIndex);
        await this.fetchAddressPage(lastId, false);
    };

    private async fetchAddressPage(lastId: number | undefined, isNewSearch: boolean) {
        this.addressLoading$.next(true);
        try {
            const { rawCustomerWithOffice, nextLastId, pageCount } =
                await this.customerDataService.fetchCustomersPage(this.currentSearch, AddressSearchPageSize, lastId, null, null, true);

            if (isNewSearch && pageCount != null) {
                this.addressPageCount$.next(pageCount);
            }
            if (nextLastId) {
                this.pageLastIds[this.currentPageIndex + 1] = nextLastId;
            }

            const options: AddressOption[] = [];
            rawCustomerWithOffice.forEach((c) => {
                (c.Offices ?? [])
                    .filter((o) => o.Street)
                    .forEach((o) => {
                        const address: Address = { Street: o.Street, PostalCode: o.PostalCode, City: o.City, Country: o.Country };
                        options.push({
                            value: address,
                            optionLabel: [c.Name, '-', o.Street, o.PostalCode, o.City, o.Country].filter((p) => !!p).join(' '),
                            searchValues: [c.Name, o.Street, o.PostalCode, o.City, o.Country].filter((p): p is string => !!p),
                        });
                    });
            });
            console.log("TEST rawCustomerWithOffice",rawCustomerWithOffice);
            console.log("TEST options",options);
            this.addressOptions$.next(options);
        } finally {
            this.addressLoading$.next(false);
        }
    }

    onTouched = () => {};
    onChange: (c: any) => void;
    private onValidatorChange: () => void;

    validate(): ValidationErrors | null {
        return this.form.valid ? null : { invalidAddress: true };
    }

    registerOnValidatorChange(fn: () => void) {
        this.onValidatorChange = fn;
    }

    onChangeSub: Subscription;

    form = new FormGroup({
        Street: new FormControl<string>(null),
        PostalCode: new FormControl<string>(null),
        City: new FormControl<string>(null),
        Country: new FormControl<string>(null),
        // AddressSupplement:  new FormControl<string>(null),
        // Longitude:  new FormControl<string>(null),
        // Latitude:  new FormControl<string>(null),
    });
    compareOptions = (a: SelectSearchOption<{ searchValues?: string[]; value: Address }>, b: SelectSearchOption<{ searchValues?: string[]; value: Address }>) => {
        return a && b && this.compareAddress(a.value, b.value);
    };
    compareAddress = (a: Address, b: Address) => a.Street === b.Street && a.PostalCode === b.PostalCode && a.City === b.City && a.Country === b.Country;
    searchFunction = (search: string, option: SelectSearchOption<{ searchValues?: string[]; value: Address }>) =>
        option.searchValues ? option.searchValues.some((s) => stringSearch(s, search)) : stringSearch(option.optionLabel as string, search);

    constructor(private customerDataService: CustomerDataService, private cdr: ChangeDetectorRef) {}

    registerOnChange(onChange: any) {
        this.onChange = onChange;
        this.onChangeSub = this.form.valueChanges.subscribe((val) => {
            onChange(val);
        });
    }

    ngOnDestroy() {
        this.onChangeSub.unsubscribe();
    }

    writeValue(value: Address) {
        if (value && !this.compareAddress(value, this.form.value as Address)) {
            this.form.setValue(
                {
                    Street: value.Street,
                    PostalCode: value.PostalCode,
                    City: value.City,
                    Country: value.Country,
                    // AddressSupplement: value.AddressSupplement,
                    // Longitude: value.Longitude,
                    // Latitude: value.Latitude,
                },
                { emitEvent: false },
            );
            // Der Wert kommt von außen (z.B. Adresse aus Kunde/Über-Auftrag übernehmen).
            // Ohne markForCheck werden die mat-form-fields nicht neu geprüft, wenn diese Component
            // in einer OnPush-View liegt – der Placeholder würde dann über dem Wert stehen bleiben.
            this.cdr.markForCheck();
        }
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    setDisabledState(disabled: boolean) {
        if (disabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    selectionChange(event: AddressOption | null) {
        const val = event;
        if (val) {
            this.form.setValue(
                {
                    Street: val.value.Street || null,
                    PostalCode: val.value.PostalCode || null,
                    City: val.value.City || null,
                    Country: val.value.Country || null,
                },
                { emitEvent: true }
            );
        }
    }

}
